import { Box } from "@chakra-ui/react";
import { ReactNode } from "react";

/**
 * Radio + RadioGroup — single-select control: 16px circle, neutral.300 border;
 * checked → success.500 border with an 8px success.500 dot and a success.50 row
 * tint. Built from Box rather than Chakra's Radio for pixel-level chrome control.
 */
export interface RadioGroupProps {
  children: ReactNode;
  "aria-label"?: string;
}

export const RadioGroup = ({
  children,
  "aria-label": ariaLabel,
}: RadioGroupProps) => (
  <Box
    role="radiogroup"
    aria-label={ariaLabel}
    display="flex"
    flexDirection="column"
    gap="2xs"
  >
    {children}
  </Box>
);

export interface RadioProps {
  checked: boolean;
  onChange: () => void;
  children: ReactNode;
  disabled?: boolean;
}

const Radio = ({ checked, onChange, children, disabled }: RadioProps) => (
  <Box
    as="button"
    type="button"
    role="radio"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    display="flex"
    alignItems="center"
    gap="sm"
    w="full"
    px="sm"
    py="2xs"
    textAlign="left"
    fontFamily="inherit"
    border="none"
    borderRadius="component.lg"
    bg={checked ? "success.50" : "transparent"}
    cursor={disabled ? "not-allowed" : "pointer"}
    opacity={disabled ? 0.5 : 1}
    transition="background-color 120ms"
    _hover={{ bg: checked ? "success.50" : "neutral.100" }}
    _focusVisible={{ outline: "none", boxShadow: "focusRing" }}
  >
    <Box
      position="relative"
      w="16px"
      h="16px"
      flexShrink={0}
      borderRadius="full"
      borderWidth="1px"
      borderStyle="solid"
      borderColor={checked ? "success.500" : "neutral.300"}
      bg="neutral.0"
      transition="border-color 120ms"
    >
      {checked && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          w="8px"
          h="8px"
          borderRadius="full"
          bg="success.500"
          transform="translate(-50%, -50%)"
        />
      )}
    </Box>
    <Box as="span" fontSize="md" color="neutral.900">
      {children}
    </Box>
  </Box>
);

export default Radio;
